import React, { Component, ErrorInfo, ReactNode } from "react";

import {
  Button,
  Flex,
  Spacer,
  Text,
  Heading,
  Illustration,
} from "@kazoohr/confetti";
import { isRecord } from "@kazoohr/helpers";
import { useTranslation } from "@kazoohr/internationalization";
import { reloadFullPage } from "@kazoohr/router";

import styles from "./errorBoundary.scss";

// A stale lazy-loaded chunk (JS or CSS) after a mid-session deploy throws here.
// Mirrors @kazoohr/errors' maybeHandleOfflineError: reload to pull the fresh
// asset manifest instead of stranding the user on the error screen. WT-29693.
function isOfflineChunkError(error?: Error): boolean {
  const messageDetails = `${error?.message} ${error?.name}`;
  return (
    messageDetails.includes("ChunkLoadError") ||
    messageDetails.includes("Loading CSS chunk")
  );
}

const RELOAD_STATE_STORAGE_KEY = "kazooChunkErrorReloads";
const MAX_RELOADS_PER_WINDOW = 2;
const RELOAD_WINDOW_MS = 60_000;

interface ReloadState {
  count: number;
  firstReloadAt: number;
}

/**
 * Increments reload counter and returns true if reloads are still allowed,
 * false if the limit has been reached.
 */
function recordReloadAttempt(): boolean {
  try {
    const now = Date.now();
    const state = getReloadState(now);
    if (state.count >= MAX_RELOADS_PER_WINDOW) {
      return false;
    }
    window.sessionStorage.setItem(
      RELOAD_STATE_STORAGE_KEY,
      JSON.stringify({
        count: state.count + 1,
        firstReloadAt: state.firstReloadAt,
      })
    );
    return true;
  } catch {
    // If storage is unavailable or unreadable we can't track reloads, so we
    // just don't reload to avoid a potential infinite loop.
    return false;
  }
}

function getReloadState(now: number): ReloadState {
  const freshState: ReloadState = { count: 0, firstReloadAt: now };
  const raw = window.sessionStorage.getItem(RELOAD_STATE_STORAGE_KEY);
  if (!raw) {
    return freshState;
  }
  const parsed: unknown = JSON.parse(raw);
  if (
    !isRecord(parsed) ||
    typeof parsed.count !== "number" ||
    typeof parsed.firstReloadAt !== "number" ||
    now - parsed.firstReloadAt > RELOAD_WINDOW_MS
  ) {
    return freshState;
  }
  return { count: parsed.count, firstReloadAt: parsed.firstReloadAt };
}

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    if (isOfflineChunkError(error)) {
      // Reload the page if a new deployment means the user's tab can no longer
      // find chunks for the old build. We need to limit the number of reloads,
      // however, otherwise this can also cause a runaway reload loop:
      // ChunkLoadError -> `reloadFullPage` -> aborts in-flight loads -> loop...
      if (recordReloadAttempt()) {
        reloadFullPage();
      }
      return;
    }
    // eslint-disable-next-line no-console
    console.error("ErrorBoundary caught an error:", error, errorInfo);
  }

  handleReload = (): void => {
    reloadFullPage();
  };

  override render(): ReactNode {
    if (this.state.hasError) {
      return (
        <ErrorBoundaryFallback
          error={this.state.error}
          onReload={this.handleReload}
        />
      );
    }

    return this.props.children;
  }
}

interface ErrorBoundaryFallbackProps {
  error?: Error;
  onReload: () => void;
}

function ErrorBoundaryFallback({
  error,
  onReload,
}: ErrorBoundaryFallbackProps) {
  const { t } = useTranslation();
  return (
    <Flex
      direction="column"
      alignItems="center"
      justifyContent="center"
      className={styles.container}
    >
      <div className={styles.illustration}>
        <Illustration illustration="okrs" />
      </div>
      <Spacer size="large" />
      <Heading level="1">{t("content:errorBoundary.title")}</Heading>
      <Spacer size="tiny" />
      <Text paragraph className={styles.message}>
        {t("content:errorBoundary.description")}
      </Text>
      {error && (
        <>
          <Spacer size="small" />
          <Text size="paragraphSmall" className={styles.errorDetails}>
            {error.message}
          </Text>
        </>
      )}
      <Spacer size="large" />
      <Button variant="primary" onClick={onReload}>
        {t("content:errorBoundary.reloadPage")}
      </Button>
    </Flex>
  );
}
